/**
 * Guideline-directed medical therapy (GDMT) advisory list. Renders the backend's
 * NON-DIRECTIVE gdmt_gaps (organ roll-up structured_fields) — a potential
 * guideline therapy that appears absent for a documented indication. It is framed
 * throughout as a prompt to REVIEW, never a directive: each entry names its
 * guideline and carries the "verify contraindications" caveat, and the whole
 * feature is flag-gated off until a clinician signs off the rule set (so this
 * usually renders nothing).
 */

import { ClipboardCheck, Info } from 'lucide-react';

import { S2Card } from './S2Card';

export interface GdmtGap {
  therapy_class: string;
  indication: string;
  status: string;
  directive?: boolean;
  provisional?: boolean;
  guideline: string;
  advisory: string;
}

/** Pull the advisory gaps out of a parsed organ `structured_fields`. */
export function readGdmtGaps(structured: unknown): GdmtGap[] {
  if (!structured || typeof structured !== 'object') return [];
  const v = (structured as Record<string, unknown>).gdmt_gaps;
  if (!Array.isArray(v)) return [];
  return v.filter(
    (g): g is GdmtGap =>
      Boolean(g) && typeof g === 'object' && typeof (g as GdmtGap).therapy_class === 'string',
  );
}

export function GdmtAdvisoryList({ gaps }: { gaps: GdmtGap[] }) {
  if (!gaps.length) return null;
  return (
    <S2Card title="Guideline-therapy review" count={gaps.length} unit="prompt" icon={ClipboardCheck}>
      {/* Advisory framing, intentionally NOT an alert colour — a review prompt,
          not a critical finding. */}
      <div className="mb-3 flex items-start gap-1.5 rounded border border-band-mild/30 bg-band-mild-bg px-2.5 py-1.5 text-[11px] leading-snug text-band-mild-text">
        <Info className="mt-0.5 size-3 shrink-0" aria-hidden />
        <span>
          Non-directive prompts for review — verify the indication, prior intolerance, and
          contraindications before acting. Not a prescribing recommendation.
        </span>
      </div>
      <ul className="divide-y divide-border/45">
        {gaps.map((g, i) => (
          <li key={`${g.therapy_class}-${i}`} className="py-2.5 first:pt-0 last:pb-0">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-medium text-text-primary">{g.therapy_class}</p>
              <span className="shrink-0 rounded border border-border bg-surface-sunken px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-text-tertiary">
                Advisory
              </span>
            </div>
            <p className="mt-0.5 text-[12px] text-text-secondary">
              <span className="text-text-tertiary">Indication:</span> {g.indication}
            </p>
            <p className="mt-0.5 text-[11px] text-text-tertiary">
              Per {g.guideline} · verify indication &amp; contraindications
            </p>
          </li>
        ))}
      </ul>
    </S2Card>
  );
}
